import React, { useEffect, createRef, useRef, useState } from 'react';
import LoadScript from 'react-load-script';
import styled, { css, useTheme } from 'styled-components';
import urljoin from 'url-join';

import { useConfig } from 'Context/ConfigContext';
import { useClassroomContext } from 'Context/ClassroomContext';
import fetch2, { METHOD } from 'Util/fetch';
import { useMyCourseData } from 'Domain/MyCourseData';

import { convertToDiscussionTheme } from './convertToDiscussionTheme';

const DISCUSSION_NODE = 'classroom-feature-discussion';
const DISCUSSION_STATE = {
  INITIALIZE: 'INITIALIZE',
  ACTIVE: 'ACTIVE',
  IDLE: 'IDLE',
};

const DiscussionNode = styled.div`
  ${({ $state, $isMinimize, $height, theme }) => {
    let cssString = css`
      & > #discussion-block {
        transition: all 0.5s;
        padding: 16px;
        height: 60px;
        @media screen and (min-width: ${theme.breakpoints[1]}) {
          padding: 16px 32px;
        }
      }
    `;

    if ($state === DISCUSSION_STATE.ACTIVE) {
      return css`
        & {
          z-index: 5;
          position: fixed;
          height: 100%;
          width: 100%;
          top: 0;
          left: 0;
          & > #discussion-block {
            transition: width 0.3s ease-in-out;
          }
          @media screen and (min-width: ${theme.breakpoints[1]}) {
            height: ${$height}px;
            width: 100%;
            min-width: inherit;
            position: absolute;
            z-index: 0;
          }
        }
      `;
    }

    if ($isMinimize) {
      cssString += css`
        & > #discussion-block {
          width: 56px;
          padding: 16px;
        }
        & > #discussion-block > p {
          display: none;
        }
      `;
    }

    return cssString;
  }}
`;

const token = createRef();
const loadDiscussionApp = async (userApi, options) => {
  const container = document.querySelector(`#${DISCUSSION_NODE}`);
  if (!container) return;

  const DiscussionApp = window?.ClassroomFeatureDiscussion?.App;
  if (!DiscussionApp || typeof DiscussionApp.render !== 'function') return;

  if (!token.current) {
    token.current = await fetch2(urljoin(userApi, '/me/session'), {}, METHOD.POST, null, {
      isRedirect: false,
      includeCredentials: true,
    });
  }

  options.accessToken = token.current;
  DiscussionApp.render(container, options);
};

const DiscussionModule = ({ isMinimize }) => {
  const moduleRef = useRef(null);
  const heightRef = useRef(0);
  const [state, setState] = useState(DISCUSSION_STATE.INITIALIZE);
  const { currentSubsectionReferId } = useClassroomContext();
  const { courseCode } = useMyCourseData();
  const { classroom, userApi } = useConfig();
  const classroomTheme = useTheme();
  const theme = convertToDiscussionTheme(classroomTheme);

  const discussionAPI = classroom?.discussion?.api;
  const identifierId = classroom?.discussion?.identifierId;
  const moduleSrc = classroom?.discussion?.src;
  const conditions = classroom?.discussion?.conditions;

  useEffect(() => {
    if (!isMinimize) return;
    moduleRef?.current?.deActivate?.();
  }, [isMinimize]);

  useEffect(() => {
    const syncState = (event) => {
      if (event.data.type !== 'DISCUSSION_STATE_CHANGE') return;

      const sidebar = document.getElementById('sidebar');
      const sidebarFooter = sidebar?.lastChild;
      const sidebarHeight = sidebar?.getBoundingClientRect?.()?.height;
      const footerHeight = sidebarFooter?.getBoundingClientRect?.()?.height;
      const spaceLeft = sidebarHeight - footerHeight;

      heightRef.current = spaceLeft + 60;
      setState(event.data.payload);
    };

    window.addEventListener('message', syncState);
    window.addEventListener('resize', () => moduleRef?.current?.deActivate());
    return () => {
      window.removeEventListener('message', syncState);
      window.removeEventListener('resize', () => moduleRef?.current?.deActivate());
    };
  }, []);

  useEffect(() => {
    if (!moduleSrc) console.error('Discussion module was enable but src was not found');
    if (!discussionAPI) console.error('Discussion module was enable but api was not found');
    if (!identifierId) console.error('Discussion module was enable but identifierId was not found');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDiscussionCourse = (conditions?.courses ?? []).includes(courseCode);
  if (!moduleSrc || !identifierId || !discussionAPI || !isDiscussionCourse) return null;
  return (
    <>
      <DiscussionNode
        id={DISCUSSION_NODE}
        $state={state}
        $isMinimize={isMinimize}
        $height={heightRef.current}
      />
      <LoadScript
        url={moduleSrc}
        key={currentSubsectionReferId}
        onLoad={() =>
          loadDiscussionApp(userApi, {
            theme,
            discussionAPI,
            identifierId,
            subsectionReferId: currentSubsectionReferId,
            ref: moduleRef,
          })
        }
      />
    </>
  );
};

export { DISCUSSION_NODE };
export default DiscussionModule;
